import * as React from "react";
import { addDays, addMonths, format, startOfDay } from "date-fns";
import type { Locale } from "date-fns";
import { useTranslation } from "react-i18next";

import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { cn } from "@/lib/utils";

type RelativePreset = {
  id: string;
  kind: "relative";
  labelKey: string;
  offset: { days?: number; months?: number };
};

type EventPreset = {
  id: string;
  kind: "event";
  labelKey: string;
  monthIndex: number;
  day: number;
};

type Preset = RelativePreset | EventPreset;

const RELATIVE_PRESETS: RelativePreset[] = [
  { id: "today", kind: "relative", labelKey: "deal.datePresets.today", offset: { days: 0 } },
  { id: "plus3Days", kind: "relative", labelKey: "deal.datePresets.plus3Days", offset: { days: 3 } },
  { id: "plus7Days", kind: "relative", labelKey: "deal.datePresets.plus7Days", offset: { days: 7 } },
  { id: "plus14Days", kind: "relative", labelKey: "deal.datePresets.plus14Days", offset: { days: 14 } },
  { id: "plus1Month", kind: "relative", labelKey: "deal.datePresets.plus1Month", offset: { months: 1 } },
  { id: "plus2Months", kind: "relative", labelKey: "deal.datePresets.plus2Months", offset: { months: 2 } },
  { id: "plus3Months", kind: "relative", labelKey: "deal.datePresets.plus3Months", offset: { months: 3 } },
];

const EVENT_PRESETS: EventPreset[] = [
  { id: "beforePriceIncrease", kind: "event", labelKey: "deal.datePresets.beforePriceIncrease", monthIndex: 1, day: 10 },
  { id: "spring", kind: "event", labelKey: "deal.datePresets.spring", monthIndex: 2, day: 1 },
  { id: "summer", kind: "event", labelKey: "deal.datePresets.summer", monthIndex: 5, day: 1 },
  { id: "autumn", kind: "event", labelKey: "deal.datePresets.autumn", monthIndex: 8, day: 1 },
  { id: "winter", kind: "event", labelKey: "deal.datePresets.winter", monthIndex: 11, day: 1 },
];

function nextFutureOccurrence(monthIndex: number, day: number, now: Date = new Date()): Date {
  const today = startOfDay(now);
  let candidate = new Date(today.getFullYear(), monthIndex, day);
  if (candidate < today) {
    candidate = new Date(today.getFullYear() + 1, monthIndex, day);
  }
  return candidate;
}

function presetDate(preset: Preset, now: Date = new Date()): Date {
  if (preset.kind === "relative") {
    if (preset.offset.months) return addMonths(now, preset.offset.months);
    return addDays(now, preset.offset.days ?? 0);
  }
  return nextFutureOccurrence(preset.monthIndex, preset.day, now);
}

export interface ClosingDatePickerContentProps {
  /** ISO date string `yyyy-MM-dd` (or empty). */
  value: string;
  onChange: (value: string) => void;
  /** Required when `showFooter` (default true). */
  onCancel?: () => void;
  /** Required when `showFooter` (default true). */
  onSave?: () => void;
  saving?: boolean;
  locale?: Locale;
  /** Compact paddings — used inside the mobile popover variant. */
  compact?: boolean;
  /**
   * Show the Cancel/Save footer. Defaults to `true`. When `false`, preset
   * clicks and calendar selections apply immediately via `onChange` (inline
   * form usage).
   */
  showFooter?: boolean;
  /**
   * `stacked` — presets above the calendar (narrow panels, e.g. global search).
   * Default `horizontal` — presets in a left column beside the calendar.
   */
  layout?: "horizontal" | "stacked";
}

export function ClosingDatePickerContent({
  value,
  onChange,
  onCancel,
  onSave,
  saving = false,
  locale,
  compact = false,
  showFooter = true,
  layout = "horizontal",
}: ClosingDatePickerContentProps) {
  const stacked = layout === "stacked";
  const { t } = useTranslation();

  const selectedDate = React.useMemo(
    () => (value ? new Date(value + "T00:00:00") : undefined),
    [value],
  );

  // Controlled displayed month so preset clicks jump the calendar to the
  // selected day's month. The user can still navigate freely with the arrows.
  const [displayMonth, setDisplayMonth] = React.useState<Date>(
    () => selectedDate ?? new Date(),
  );

  // Sync displayed month when `value` changes from outside (preset click or
  // parent state update). Compare month/year to avoid fighting the user's
  // manual arrow navigation when only the day-of-month changed via calendar
  // click.
  React.useEffect(() => {
    if (!selectedDate) return;
    setDisplayMonth((current) => {
      if (
        current.getFullYear() === selectedDate.getFullYear() &&
        current.getMonth() === selectedDate.getMonth()
      ) {
        return current;
      }
      return selectedDate;
    });
  }, [selectedDate]);

  const applyPreset = React.useCallback(
    (preset: Preset) => {
      const next = presetDate(preset);
      onChange(format(next, "yyyy-MM-dd"));
      setDisplayMonth(next);
    },
    [onChange],
  );

  const activePresetId = React.useMemo(() => {
    if (!value) return null;
    const now = new Date();
    const all: Preset[] = [...RELATIVE_PRESETS, ...EVENT_PRESETS];
    for (const p of all) {
      if (format(presetDate(p, now), "yyyy-MM-dd") === value) return p.id;
    }
    return null;
  }, [value]);

  const presetButton = (preset: Preset) => {
    const active = preset.id === activePresetId;
    return (
      <button
        key={preset.id}
        type="button"
        onClick={() => applyPreset(preset)}
        className={cn(
          "text-sm rounded px-2 py-1 text-left transition-colors hover:bg-muted",
          active && "bg-muted font-medium",
        )}
      >
        {t(preset.labelKey)}
      </button>
    );
  };

  const presetList = (
    <>
      {RELATIVE_PRESETS.map(presetButton)}
      <div
        className={cn(
          "border-t my-1",
          stacked && "w-full basis-full sm:w-auto sm:basis-auto",
        )}
      />
      {EVENT_PRESETS.map(presetButton)}
    </>
  );

  return (
    <div className={cn(compact ? "p-3" : "p-4")}>
      <div
        className={cn(
          "flex items-start gap-3",
          stacked && "max-w-[min(100%,20rem)] flex-col gap-3 sm:max-w-none sm:flex-row",
        )}
      >
        <div
          className={cn(
            stacked
              ? "flex w-full flex-wrap gap-1 border-b border-border pb-3 sm:w-auto sm:flex-col sm:flex-nowrap sm:gap-0.5 sm:border-b-0 sm:border-r sm:pb-0 sm:pr-3"
              : "flex flex-col gap-0.5 border-r pr-3",
            !stacked && (compact ? "min-w-[100px]" : "min-w-[112px]"),
          )}
        >
          {presetList}
        </div>
        <CalendarComponent
          mode="single"
          selected={selectedDate}
          onSelect={(date) => {
            if (date) onChange(format(date, "yyyy-MM-dd"));
          }}
          month={displayMonth}
          onMonthChange={setDisplayMonth}
          locale={locale}
          initialFocus
          className={cn(stacked && "mx-auto sm:mx-0")}
        />
      </div>
      {showFooter && (
        <div
          className={cn(
            "flex items-center justify-end border-t gap-2",
            compact ? "px-3 py-2 mt-2" : "px-4 py-3 mt-3",
          )}
        >
          <button
            type="button"
            onClick={onCancel}
            className="px-3 py-1.5 rounded-md text-sm font-medium hover:bg-muted transition-colors"
          >
            {t("common.cancel")}
          </button>
          <button
            type="button"
            onClick={onSave}
            disabled={saving}
            className="px-3 py-1.5 rounded-md text-sm font-medium bg-primary text-primary-foreground hover:opacity-90 disabled:opacity-50 transition-colors"
          >
            {t("common.save")}
          </button>
        </div>
      )}
    </div>
  );
}

export default ClosingDatePickerContent;
